import { useEffect, useState, useCallback } from 'react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'

interface LinhaOrdem {
  est: number
  numero: number
  fantasia: string
  fornecedor: string
  dtemis: string
  qtetotal: string
  vltotal: string
  comprador: string
  situacao: string
  situacaoLabel: string
  aprovacao?: 'A' | 'L' | 'R'
  aprovacaoLabel?: string
}

interface Filtros {
  empcodDe: string
  empcodAte: string
  dataDe: string
  dataAte: string
  numero: string
  compradorCod: string
  compradorDes: string
  fornecedorCod: string
  fornecedorDes: string
  item: string
  veiculoCod: string
  tipo: string
  situacao: string
}

function hojeISO() { return new Date().toISOString().slice(0, 10) }

function filtroPadrao(): Filtros {
  return {
    empcodDe: '1', empcodAte: '999', dataDe: hojeISO(), dataAte: hojeISO(),
    numero: '', compradorCod: '', compradorDes: '', fornecedorCod: '', fornecedorDes: '',
    item: '', veiculoCod: '', tipo: '', situacao: '',
  }
}

function fmtData(iso: string | null): string {
  if (!iso || iso.slice(0, 4) <= '0001') return '-'
  return iso.slice(0, 10).split('-').reverse().join('/')
}
function fmtQtd(v: string): string {
  return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3 })
}
function fmtVal(v: string): string {
  return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
}

const SITUACAO_COR: Record<string, { cor: string; fundo: string }> = {
  Aberto: { cor: '#991b1b', fundo: '#fee2e2' },
  Cancelado: { cor: '#6b7280', fundo: '#f3f4f6' },
  Atendido: { cor: '#15803d', fundo: '#dcfce7' },
}

const TIPO_COBRANCA_DES: Record<string, string> = {
  B: 'Boleto Bancário', R: 'Cartão de Crédito', F: 'Cobrança Bancária', P: 'Pix',
  Q: 'Cheque', D: 'Depósito Conta', H: 'Dinheiro', C: 'Em Carteira',
}
const INDICADOR_FRETE_DES: Record<string, string> = {
  '9': 'Sem Cobrança de Frete', '1': 'Pago pelo Dest./Remetente', '0': 'Pago pelo Emitente', '2': 'Pago por Terceiros',
}

function Grupo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <label style={{
        fontSize: '0.66rem', fontWeight: 700, color: '#1a6ea8',
        textTransform: 'uppercase', letterSpacing: '0.05em',
      }}>
        {label}
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
        {children}
      </div>
    </div>
  )
}

export default function ConsultaOrdemCompra() {
  const [lista, setLista] = useState<LinhaOrdem[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const [filtros, setFiltros] = useState<Filtros>(filtroPadrao)
  const [pesquisaAtiva, setPesquisaAtiva] = useState<Filtros>(filtroPadrao)

  function paramsDeFiltro(f: Filtros): Record<string, string> {
    const params: Record<string, string> = {}
    if (f.empcodDe) params.empcodDe = f.empcodDe
    if (f.empcodAte) params.empcodAte = f.empcodAte
    if (!f.numero) {
      if (f.dataDe) params.dataDe = f.dataDe
      if (f.dataAte) params.dataAte = f.dataAte
    }
    if (f.numero) params.numero = f.numero
    if (f.compradorCod) params.compradorCod = f.compradorCod
    if (f.fornecedorCod) params.fornecedorCod = f.fornecedorCod
    if (f.item) params.item = f.item
    if (f.veiculoCod) params.veiculoCod = f.veiculoCod
    if (f.tipo) params.tipo = f.tipo
    if (f.situacao) params.situacao = f.situacao
    return params
  }

  const carregar = useCallback(async () => {
    setCarregando(true); setErro(null)
    try {
      const r = await api.get('/erp/ordem-compra/lista', { params: paramsDeFiltro(pesquisaAtiva) })
      setLista(r.data)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao carregar lista')
    } finally {
      setCarregando(false)
    }
  }, [pesquisaAtiva])

  useEffect(() => { carregar() }, [carregar])

  function pesquisar() { setPesquisaAtiva({ ...filtros }) }
  function limpar() { const p = filtroPadrao(); setFiltros(p); setPesquisaAtiva(p) }

  function abrirPdfListagem() {
    const qs = new URLSearchParams(paramsDeFiltro(pesquisaAtiva)).toString()
    window.open(`/api/erp/ordem-compra/lista/pdf${qs ? '?' + qs : ''}`, '_blank')
  }

  const [resumo, setResumo] = useState<{ est: number; numero: number } | null>(null)
  const [resumoDados, setResumoDados] = useState<any>(null)
  const [resumoCarregando, setResumoCarregando] = useState(false)

  async function abrirResumo(est: number, numero: number) {
    setResumo({ est, numero }); setResumoDados(null); setResumoCarregando(true)
    try {
      const r = await api.get(`/erp/ordem-compra/${est}/${numero}`)
      setResumoDados(r.data)
    } catch {
      setResumoDados(null)
    } finally {
      setResumoCarregando(false)
    }
  }
  function fecharResumo() { setResumo(null); setResumoDados(null) }

  const [confirmarCancelamento, setConfirmarCancelamento] = useState(false)
  const [cancelando, setCancelando] = useState(false)
  const [erroCancelar, setErroCancelar] = useState<string | null>(null)

  async function cancelarOrdem() {
    if (!resumo) return
    setCancelando(true); setErroCancelar(null)
    try {
      await api.post(`/erp/ordem-compra/${resumo.est}/${resumo.numero}/cancelar`)
      setConfirmarCancelamento(false)
      fecharResumo()
      await carregar()
    } catch (e: any) {
      setErroCancelar(e?.response?.data?.error || 'Erro ao cancelar a ordem')
    } finally {
      setCancelando(false)
    }
  }

  const inp: React.CSSProperties = {
    border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
    fontSize: '0.82rem', height: 30, background: '#fff',
  }
  const th: React.CSSProperties = {
    padding: '9px 10px', textAlign: 'left', color: '#fff', fontWeight: 700,
    fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap',
  }
  const td: React.CSSProperties = { padding: '8px 10px', color: '#374151' }

  return (
    <div className="card">
      <h2 style={{
        fontSize: '1.1rem', fontWeight: 700, margin: '0 0 1rem', color: '#1a6ea8',
        paddingBottom: '0.6rem', borderBottom: '2px solid #eff6ff',
      }}>
        Consultar Ordens de Compra
      </h2>

      <div style={{
        background: '#f7faff', border: '1px solid #eef0f4', borderLeft: '4px solid #1a6ea8',
        borderRadius: '0.5rem', padding: '0.7rem 0.9rem', marginBottom: '1rem',
      }}>
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '0.6rem 1rem', alignItems: 'flex-end', maxWidth: 920,
      }}>
        <Grupo label="Estabelecimento">
          <input style={{ ...inp, width: 55 }} type="number" value={filtros.empcodDe}
            onChange={e => setFiltros(f => ({ ...f, empcodDe: e.target.value }))} />
          <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>a</span>
          <input style={{ ...inp, width: 55 }} type="number" value={filtros.empcodAte}
            onChange={e => setFiltros(f => ({ ...f, empcodAte: e.target.value }))} />
        </Grupo>
        <Grupo label="Data Emissão">
          <input style={{ ...inp, width: 130 }} type="date" value={filtros.dataDe}
            onChange={e => setFiltros(f => ({ ...f, dataDe: e.target.value }))} />
          <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>a</span>
          <input style={{ ...inp, width: 130 }} type="date" value={filtros.dataAte}
            onChange={e => setFiltros(f => ({ ...f, dataAte: e.target.value }))} />
        </Grupo>
        <Grupo label="Número">
          <input style={{ ...inp, width: 90 }} type="number" value={filtros.numero}
            onChange={e => setFiltros(f => ({ ...f, numero: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && pesquisar()} />
        </Grupo>
        <Grupo label="Comprador">
          <div style={{ width: 160 }}>
            <LookupSelect
              endpoint="/compradores"
              codKey="cpr_cod" descKey="cpr_des"
              renderItem={(item: any) => (
                <>
                  <div className="nat-item-linha1"><span className="nat-item-cod">{item.cpr_cod}</span></div>
                  <div className="nat-item-dsc">{item.cpr_des}</div>
                </>
              )}
              codValue={filtros.compradorCod} descValue={filtros.compradorDes}
              onSelect={(cod, des) => setFiltros(f => ({ ...f, compradorCod: cod, compradorDes: des }))}
              placeholder="Todos..." listWidth={260}
            />
          </div>
        </Grupo>
        <Grupo label="Item">
          <input style={{ ...inp, width: 80 }} type="number" value={filtros.item}
            onChange={e => setFiltros(f => ({ ...f, item: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && pesquisar()} />
        </Grupo>
        <Grupo label="Veículo">
          <div style={{ width: 130 }}>
            <LookupSelect
              endpoint="/erp/veiculos"
              codKey="veicod" descKey="veipla"
              codValue={filtros.veiculoCod} descValue={filtros.veiculoCod}
              onSelect={cod => setFiltros(f => ({ ...f, veiculoCod: cod }))}
              placeholder="Todos..." listWidth={220}
            />
          </div>
        </Grupo>
        <Grupo label="Fornecedor">
          <div style={{ width: 200 }}>
            <LookupSelect
              endpoint="/erp/clientes/lista"
              codKey="cli_cod" descKey="cli_fan"
              renderItem={(item: any) => (
                <>
                  <div className="nat-item-linha1"><span className="nat-item-cod">{item.cli_mat}</span></div>
                  <div className="nat-item-dsc">{item.cli_fan || item.cli_nom}</div>
                </>
              )}
              codValue={filtros.fornecedorCod} descValue={filtros.fornecedorDes}
              onSelect={(cod, des) => setFiltros(f => ({ ...f, fornecedorCod: cod, fornecedorDes: des }))}
              placeholder="Todos..." listWidth={320} somenteComTexto
            />
          </div>
        </Grupo>
        <Grupo label="Tipo">
          <input style={{ ...inp, width: 55, textTransform: 'uppercase' }} maxLength={1} value={filtros.tipo}
            onChange={e => setFiltros(f => ({ ...f, tipo: e.target.value.toUpperCase() }))}
            onKeyDown={e => e.key === 'Enter' && pesquisar()} />
        </Grupo>
        <Grupo label="Situação">
          <select style={{ ...inp, width: 130 }} value={filtros.situacao}
            onChange={e => setFiltros(f => ({ ...f, situacao: e.target.value }))}>
            <option value="">Todas</option>
            <option value="00">Aberto</option>
            <option value="OK">Atendido</option>
            <option value="CA">Cancelado</option>
          </select>
        </Grupo>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.7rem' }}>
        <button className="btn btn-primary btn-sm" onClick={pesquisar} disabled={carregando}
          style={{ height: 30 }}>
          {carregando ? 'Buscando...' : 'Pesquisar'}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={limpar}
          style={{ height: 30 }}>
          Limpar
        </button>
        <button className="btn btn-sm" onClick={abrirPdfListagem}
          disabled={lista.length === 0}
          style={{ height: 30, background: '#dc2626', color: '#fff' }}>
          Gerar PDF
        </button>
      </div>
      </div>

      {erro && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erro}</div>}

      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
        <table style={{ fontSize: '0.8rem', minWidth: 1000, borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr style={{ background: '#1a6ea8' }}>
              <th style={{ ...th, textAlign: 'center', width: 40 }}>Est</th>
              <th style={{ ...th, width: 70 }}>Número</th>
              <th style={th}>Fantasia</th>
              <th style={th}>Fornecedor</th>
              <th style={{ ...th, width: 80 }}>Dt.Emis</th>
              <th style={{ ...th, textAlign: 'right', width: 90 }}>Qte Total</th>
              <th style={{ ...th, textAlign: 'right', width: 100 }}>Vl Total</th>
              <th style={{ ...th, width: 150 }}>Comprador</th>
              <th style={{ ...th, width: 90 }}>Sit</th>
              <th style={{ ...th, width: 150 }}>Aprovação</th>
            </tr>
          </thead>
          <tbody>
            {carregando && (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Carregando...</td></tr>
            )}
            {!carregando && lista.length === 0 && (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Nenhuma ordem de compra encontrada</td></tr>
            )}
            {lista.map((row, idx) => {
              const cor = SITUACAO_COR[row.situacaoLabel] ?? { cor: '#374151', fundo: '#f3f4f6' }
              return (
                <tr key={`${row.est}-${row.numero}`} style={{
                  background: idx % 2 === 0 ? '#fff' : '#f7faff',
                  borderBottom: '1px solid #e5e7eb',
                }}>
                  <td style={{ ...td, textAlign: 'center' }}>{row.est}</td>
                  <td style={{ ...td, fontWeight: 700 }}>
                    <span onClick={() => abrirResumo(row.est, row.numero)}
                      style={{ cursor: 'pointer', color: '#1d4ed8', textDecoration: 'underline' }}
                      title="Clique para ver o resumo da ordem">
                      {row.numero}
                    </span>
                  </td>
                  <td style={td}>{row.fantasia || '-'}</td>
                  <td style={td}>{row.fornecedor || '-'}</td>
                  <td style={td}>{fmtData(row.dtemis)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmtQtd(row.qtetotal)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmtVal(row.vltotal)}</td>
                  <td style={td}>{row.comprador || '-'}</td>
                  <td style={td}>
                    <span style={{
                      fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                      background: cor.fundo, color: cor.cor,
                    }}>
                      {row.situacaoLabel}
                    </span>
                  </td>
                  <td style={td}>
                    <span style={{
                      fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                      ...(row.aprovacao === 'R' ? { background: '#fef2f2', color: '#b91c1c' } : row.aprovacao === 'A' ? { background: '#fff7ed', color: '#b45309' } : { background: '#f0fdf4', color: '#15803d' }),
                    }}>
                      {row.aprovacaoLabel || 'Aprovada'}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {resumo && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          paddingTop: '4rem', zIndex: 1000,
        }} onClick={fecharResumo}>
          <div style={{ background: '#fff', borderRadius: 8, width: 680, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ background: '#1a6ea8', color: '#fff', padding: '0.9rem 1.25rem', borderRadius: '8px 8px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '1rem', fontWeight: 700 }}>Ordem de Compra nº {resumo.numero}</div>
                <div style={{ fontSize: '0.78rem', opacity: 0.85 }}>Estabelecimento {resumo.est}</div>
              </div>
              <button onClick={fecharResumo} style={{ background: 'rgba(255,255,255,0.18)', border: 'none', cursor: 'pointer', color: '#fff', borderRadius: 4, padding: '4px 10px', fontSize: '0.9rem' }}>✕</button>
            </div>

            {resumoCarregando && <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Carregando...</div>}

            {!resumoCarregando && !resumoDados && (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#b91c1c' }}>Não foi possível carregar os dados.</div>
            )}

            {!resumoCarregando && resumoDados && (
              <div style={{ padding: '1.1rem 1.25rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1.5rem', fontSize: '0.82rem', marginBottom: '0.9rem' }}>
                  <div><b>Data Emissão:</b> {fmtData(resumoDados.dataEmissao)}</div>
                  <div><b>Fornecedor:</b> {resumoDados.fornecedorDes || '-'}</div>
                  <div><b>Comprador:</b> {resumoDados.compradorDes || '-'}</div>
                  <div><b>Usuário Inclusão:</b> {resumoDados.usuarioInclusao || '-'}</div>
                  <div><b>Data Inclusão:</b> {fmtData(resumoDados.dataInclusao)}</div>
                </div>
                {resumoDados.observacao && (
                  <div style={{ fontSize: '0.82rem', marginBottom: '0.9rem' }}>
                    <b>Observação:</b> {resumoDados.observacao}
                  </div>
                )}

                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '0.7rem 0.9rem', marginBottom: '0.9rem' }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: '0.4rem' }}>
                    Financeiro
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1.5rem', fontSize: '0.82rem', marginBottom: resumoDados.parcelas?.length > 0 ? '0.9rem' : 0 }}>
                    <div><b>Condição Pgto:</b> {resumoDados.condicaoPgtoDes || '-'}</div>
                    <div><b>Tipo Cobrança:</b> {TIPO_COBRANCA_DES[resumoDados.tipoCobranca] || resumoDados.tipoCobranca || '-'}</div>
                    <div>
                      <b>Frete:</b> {INDICADOR_FRETE_DES[resumoDados.indicadorFrete] || '-'}
                      {resumoDados.indicadorFrete !== '9' && Number(resumoDados.valorFrete) > 0 && ` (R$ ${fmtVal(resumoDados.valorFrete)})`}
                    </div>
                    <div><b>Desconto:</b> {Number(resumoDados.valorDesconto) > 0 ? `R$ ${fmtVal(resumoDados.valorDesconto)}` : '-'}</div>
                  </div>

                  {resumoDados.parcelas?.length > 0 && (
                    <>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: '0.3rem' }}>
                        Parcelas ({resumoDados.parcelas.length})
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #bbf7d0' }}>
                            <th style={{ textAlign: 'left', padding: '4px 6px' }}>Parcela</th>
                            <th style={{ textAlign: 'left', padding: '4px 6px' }}>Vencimento</th>
                            <th style={{ textAlign: 'right', padding: '4px 6px' }}>Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {resumoDados.parcelas.map((p: any) => (
                            <tr key={p.parcela} style={{ borderBottom: '1px solid #dcfce7' }}>
                              <td style={{ padding: '4px 6px' }}>{p.parcela}</td>
                              <td style={{ padding: '4px 6px' }}>{fmtData(p.data)}</td>
                              <td style={{ padding: '4px 6px', textAlign: 'right' }}>R$ {fmtVal(p.valor)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>

                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1a6ea8', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: '0.4rem' }}>
                  Itens ({resumoDados.itens?.length ?? 0})
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Item</th>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Descrição</th>
                      <th style={{ textAlign: 'right', padding: '4px 6px' }}>Qtd.</th>
                      <th style={{ textAlign: 'center', padding: '4px 6px' }}>Un.</th>
                      <th style={{ textAlign: 'right', padding: '4px 6px' }}>Preço</th>
                      <th style={{ textAlign: 'right', padding: '4px 6px' }}>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resumoDados.itens?.map((it: any) => (
                      <tr key={it.linha} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '4px 6px' }}>{it.itemCod}</td>
                        <td style={{ padding: '4px 6px' }}>{it.itemDes}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'right' }}>{fmtQtd(it.quantidade)}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'center' }}>{it.unidade}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'right' }}>{fmtVal(it.precoUnitario)}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'right' }}>{fmtVal(it.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {resumoDados.aprovacao && resumoDados.aprovacao !== 'L' && (
                  <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.5rem 0.8rem', borderRadius: 6,
                    ...(resumoDados.aprovacao === 'R' ? { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' } : { background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa' }) }}>
                    {resumoDados.aprovacao === 'R' ? 'Ordem REPROVADA' : 'Ordem AGUARDANDO APROVAÇÃO'} — o comprovante só pode ser impresso depois de aprovada.
                    {resumoDados.historicoAprovacao?.[0]?.observacao && <> Motivo: {resumoDados.historicoAprovacao[0].observacao}</>}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, marginTop: '1.1rem' }}>
                  {(!resumoDados.aprovacao || resumoDados.aprovacao === 'L') && (
                    <button className="btn btn-primary btn-sm"
                      onClick={() => window.open(`/api/erp/ordem-compra/${resumo.est}/${resumo.numero}/comprovante`, '_blank')}>
                      Imprimir Comprovante
                    </button>
                  )}
                  {['', '00'].includes(resumoDados.situacaoCod) && (
                    <button
                      onClick={() => setConfirmarCancelamento(true)}
                      style={{
                        background: 'none', border: '1px solid #dc2626', color: '#dc2626',
                        borderRadius: 4, padding: '5px 14px', fontSize: '0.82rem',
                        fontWeight: 600, cursor: 'pointer',
                      }}>
                      Cancelar Ordem
                    </button>
                  )}
                  <button className="btn-editar" onClick={fecharResumo}>
                    Fechar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {confirmarCancelamento && resumo && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
        }} onClick={() => !cancelando && setConfirmarCancelamento(false)}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '1.5rem',
            width: 420, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 700, color: '#dc2626' }}>
              Cancelar Ordem de Compra
            </h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#374151' }}>
              Tem certeza que deseja cancelar a Ordem de Compra nº {resumo.numero}? Essa ação não pode ser desfeita.
            </p>
            {erroCancelar && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erroCancelar}</div>}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-sm" onClick={() => setConfirmarCancelamento(false)} disabled={cancelando}
                style={{ color: '#6b7280' }}>
                Voltar
              </button>
              <button
                onClick={cancelarOrdem}
                disabled={cancelando}
                style={{
                  background: '#dc2626', color: '#fff', border: 'none',
                  borderRadius: 4, padding: '5px 16px', fontSize: '0.82rem',
                  fontWeight: 600, cursor: cancelando ? 'not-allowed' : 'pointer',
                  opacity: cancelando ? 0.6 : 1,
                }}>
                {cancelando ? 'Cancelando...' : 'Confirmar Cancelamento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
